import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Col, Row, Spin } from 'antd';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import useColors from '../../../hooks/useColors';
import { ReportAction } from '../../../store/actions';
import BasicDrawer from '../../../components/Drawer';
import { Button } from '../../../components';
import { generateReportSchema } from '../../../config/Schema';
import { Colors, ApiCaller } from '../../../config';
import HelperText from '../../../components/form/helper-text';
import { zodResolver } from '@hookform/resolvers/zod';
import { getAllIncidents, getAllUsers } from '@config/getApis';
import {
    generateReportContent,
    generateReportDefaults,
    generateReportInputs,
    REPORT_TYPES,
} from './helper';
import { useNavigate } from 'react-router';

const GenerateReport = ({ openDrawer, onCloseDrawer, reportType, prefillValues }) => {
    const { colorPrimary } = useColors();
    const navigate = useNavigate();

    const loader = useSelector((store) => store.ReportReducer.loader);
    const { dateFormat } = useSelector((store) => store.AppReducer);

    const { data: incidentsOptions } = useQuery('incident', getAllIncidents, {
        enabled: openDrawer,
    });

    const [propPage, setPropPage] = useState(1);
    const [propOptions, setPropOptions] = useState([]);
    const [hasMoreProps, setHasMoreProps] = useState(true);
    const [propLoading, setPropLoading] = useState(false);

    const fetchProperties = async (page) => {
        if (page > 1 && !hasMoreProps) return;
        setPropLoading(true);
        try {
            let res = await ApiCaller.Get(`/properties?search=&perPage=25&page=${page}&sortBy=`);
            const props = res?.data?.data?.map(p => ({ label: p.name, value: p.id })) || [];
            const paginate = res?.data?.paginate;
            if (page === 1) {
                setPropOptions(props);
            } else {
                setPropOptions(prev => [...prev, ...props]);
            }
            if (paginate) {
                setHasMoreProps(paginate.currentPage < paginate.lastPage);
            } else {
                setHasMoreProps(false);
            }
        } catch (e) {} finally {
            setPropLoading(false);
        }
    };

    useEffect(() => {
        if (openDrawer && propOptions.length === 0) {
            fetchProperties(1);
        }
    }, [openDrawer]);

    const onPropScroll = (e) => {
        const { target } = e;
        if (target.scrollTop + target.offsetHeight + 10 >= target.scrollHeight) {
            if (hasMoreProps && !propLoading) {
                const next = propPage + 1;
                setPropPage(next);
                fetchProperties(next);
            }
        }
    };

    const [isLevelDisabled, setIsLevelDisabled] = useState(false);

    const { data: userOptions } = useQuery('users', getAllUsers, {
        enabled: reportType === REPORT_TYPES.grouped,
    });

    const inputs = useMemo(
        () => {
            if (incidentsOptions && incidentsOptions?.length > 0) {
                incidentsOptions.push({
                    label: 'All',
                    value: 'All'
                })
            }
            return generateReportInputs({
                propertyOptions: propOptions,
                incidentsOptions,
                userOptions,
                reportType,
                isLevelDisabled,
                dateFormat,
                onPropScroll,
                propLoading,
            })
        },
        [propOptions, incidentsOptions, userOptions, isLevelDisabled, propLoading],
    );
    const content = useMemo(
        () => generateReportContent({ reportType }),
        [reportType],
    );


    const { handleSubmit, control, reset, trigger, setValue, formState: { errors } } = useForm({
        resolver: zodResolver(generateReportSchema({ reportType, isIncidentLevelDisable: isLevelDisabled })),
        mode: 'onChange',
        defaultValues: generateReportDefaults,
    });

    useEffect(() => {
        reset(undefined, { keepValues: true })
    }, [isLevelDisabled])

    const onSubmit = (payload) => {
        console.log(payload)
        const data = {
            ...payload,
            startDate: payload.dates.startDate,
            endDate: payload.dates.endDate,
        };

        delete data.dates;

        if (data?.status == 'pending') {
            delete data?.level
        }

        const qParams = new URLSearchParams();

        Object?.entries(data)?.forEach(([k, v]) => {
            if (Array.isArray(v)) {
                v.forEach((val) => qParams.append(k + '[]', val));
            } else {
                qParams.append(k, v);
            }
        });

        // if level is 0 means all is selected so as per backend req, not sending that key.
        if (Number(qParams.get('level')) == 0) {
            qParams.delete('level')
        }

        qParams.append('reportType', reportType);
        navigate(`/security-issues/view-report?${qParams.toString()}`);
        onCloseDrawer();
    };

    useEffect(() => {
        if (openDrawer && prefillValues) {
            reset({ ...generateReportDefaults, ...prefillValues });
        }
    }, [openDrawer]);

    const afterCloseDrawer = (open) => {
        if (!open) {
            reset(generateReportDefaults);
        }
    };

    // in case of status pending we are disabling the level radio fields. 
    const valueTrackHandler = (name, event, onChange, transformChange) => {
        // adding a case where name is 'incidentTypeId' and value is 'All' then add all ids as value. 
        if (name === 'incidentTypeId' && event.includes('All')) {
            const allIds = incidentsOptions
                ?.map(opt => opt?.value)
                .filter(val => val && val !== 'All');
            onChange(transformChange(allIds)); // update with all IDs
            return;
        }
        let incidentStatus = event?.target?.value;
        if (incidentStatus == 'pending') {
            setIsLevelDisabled(true);
            setValue('level', undefined);
        } else if (incidentStatus == 'assigned') {
            setIsLevelDisabled(false);
        }
        onChange(transformChange(event));
    }

    console.log(errors);

    return (
        <BasicDrawer
            openDrawer={openDrawer}
            title={content.title}
            onClose={onCloseDrawer}
            afterOpenChange={afterCloseDrawer}
            width={600}
        >
            <Row className="w-full">
                <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                    <Row className="w-full flex justify-between">
                        <Row
                            className="w-full flex justify-between gap-y-4"
                            gutter={16}
                        >
                            {inputs?.length > 0 &&
                                inputs.map(
                                    ({
                                        name,
                                        placeholder,
                                        helperText = '',
                                        type = '',
                                        label = '',
                                        span = 0.5,
                                        triggerKey = '',
                                        transform: {
                                            value: transformValue,
                                            change: transformChange,
                                        } = {
                                            value: (v) => v,
                                            change: (v) => v,
                                        },
                                        component: Component,
                                        ...input
                                    }) => {
                                        return (
                                            <Col
                                                key={name}
                                                xs={24}
                                                sm={span * 24}
                                            >
                                                <label
                                                    className="font-normal"
                                                    style={{
                                                        color: Colors.LabelColor,
                                                    }}
                                                >
                                                    {label}
                                                </label>
                                                <div
                                                    className="w-full my-1"
                                                    style={{ width: '100%' }}
                                                >
                                                    <Controller
                                                        name={name}
                                                        control={control}
                                                        render={({
                                                            field: {
                                                                value,
                                                                onChange,
                                                            },
                                                            fieldState,
                                                        }) => {
                                                            console.log(transformValue(value))
                                                            return (
                                                                <>
                                                                    <Component
                                                                        value={
                                                                            transformValue(
                                                                                value
                                                                            )
                                                                        }
                                                                        onChange={(
                                                                            v,
                                                                        ) => {
                                                                            // valueTrackHandler(name, v)
                                                                            // onChange(
                                                                            //     transformChange(
                                                                            //         v,
                                                                            //     ),
                                                                            // );
                                                                            // if (name === "incidentTypeId" && v?.includes('All')) {
                                                                            //     valueTrackHandler(name, v, onChange, transformChange); // custom override
                                                                            // } else {
                                                                            //     valueTrackHandler(name, v, onChange, transformChange);
                                                                            // }
                                                                            valueTrackHandler(name, v, onChange, transformChange);

                                                                            triggerKey &&
                                                                                trigger(
                                                                                    triggerKey,
                                                                                );
                                                                        }}
                                                                        placeholder={
                                                                            placeholder
                                                                        }
                                                                        className="outline-none rounded-md h-9 w-full"
                                                                        {...input}
                                                                    />
                                                                    <HelperText>
                                                                        {
                                                                            helperText
                                                                        }
                                                                    </HelperText>
                                                                    <HelperText
                                                                        type="danger"
                                                                        className="h-[1em]"
                                                                    >
                                                                        {fieldState
                                                                            ?.error
                                                                            ?.message ??
                                                                            ''}
                                                                    </HelperText>
                                                                </>
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </Col>
                                        );
                                    },
                                )}
                        </Row>
                    </Row>

                    <Row className="mt-8">
                        <Button.Basic
                            type={'submit'}
                            className="h-12 border-none text-white border-transparent"
                            style={{ backgroundColor: colorPrimary }}
                            text={'Generate Report'}
                            loader={loader}
                        ></Button.Basic>
                        {/* <input type='submit' style={{ background: colorPrimary, color: Colors.White }} className={'cursor-pointer my-2 mx-1 h-10 rounded-none px-8'} value={drawerStates.drawerkey == 1 ? 'Update User' : 'Invite Clients'} /> */}
                    </Row>
                </form>
            </Row>
        </BasicDrawer>
    );
};

export default GenerateReport;
